import { computed, readonly, ref } from 'vue'
import { useApiConfig } from '@/config/api'
import { getAccessToken } from '~/utils/authToken'
import { sendHeldWorkFirst as decideHeldWork, type HeldWorkOutcome } from '~/utils/syncQueue/heldWorkFirst'
import { useAuthStore } from '~/stores/auth'
import { createConnectivityMonitor } from '~/utils/syncQueue/connectivity'
import { createFlushEngine } from '~/utils/syncQueue/flushEngine'
import { createHttpTransport, isLiveType, sessionHeaders } from '~/utils/syncQueue/httpTransport'
import { awaitSettlement, type Settlement } from '~/utils/syncQueue/settle'
import { newUuid } from '~/utils/uuid'
import { FAKE_BACKEND, fakeSyncTransport } from '~/utils/fakeBackend'
import { createQueueStorage } from '~/utils/syncQueue/indexedDbStorage'
import type { QueuedTransaction } from '~/utils/syncQueue/types'

/**
 * The app's entry point to store-and-forward (ID28 slice 1).
 *
 * A screen calls `enqueue()` and is done — the transaction is durable before the
 * call returns, so the operator gets their confirmation immediately and the
 * network is never in their way. Everything below this is background work.
 *
 * One shared instance per app: a second queue would race the first on the same
 * IndexedDB rows.
 */

type QueueSingleton = ReturnType<typeof createQueueSingleton>
let singleton: QueueSingleton | null = null

/** Probe cadence while we believe we are offline. */
const OFFLINE_PROBE_MS = 10_000
/** Safety-net flush while online, in case a trigger was missed. */
const IDLE_FLUSH_MS = 60_000
/** How long a screen waits on the server's answer before calling a send "queued". */
const SETTLE_TIMEOUT_MS = 6_000

function createQueueSingleton() {
  const { storage, durable } = createQueueStorage()
  const apiConfig = useApiConfig()

  const pending = ref(0)
  const needsAttention = ref(0)
  const isSyncing = ref(false)
  const needsAuth = ref(false)

  const monitor = createConnectivityMonitor({
    probe: async () => {
      try {
        // `/api/health/live` is [AllowAnonymous] and returns a bare 200 — the
        // cheapest honest answer to "can this tablet reach the API?".
        const response = await fetch(`${apiConfig.baseURL}/health/live`, { method: 'GET' })
        return response.ok
      } catch {
        return false
      }
    },
    // Reconnecting is exactly when queued work should go out.
    onOnline: () => {
      void flush()
    },
  })

  /** A lookup got no answer. Not a verdict on the connection: a reason to ask the API directly. */
  function noteUnreachable() {
    void monitor.check()
  }

  /**
   * The tablet says its Wi-Fi is back (the app reports it — see
   * plugins/device-bridge). A hint, like the browser's own: it makes us ask
   * the API now instead of at the next probe, and the answer decides.
   */
  function nudge() {
    void monitor.hint()
  }

  // The senders' paths start at `/api/...`, so the transport is given the
  // API's ORIGIN — baseURL without its `/api` — not baseURL itself.
  //
  // This used to be left out, and the transport's default of '' turned every
  // send into a RELATIVE request to the page's own origin. Under `npm run dev`
  // the Nitro dev proxy forwards `/api/*` to the backend, so it worked; under
  // the production build the tablet runs (`nuxt preview`) there is no proxy,
  // the server answers any unknown path with the app shell as a 200, and the
  // queue read that 200 as "accepted": every load sent from the tablet was
  // marked synced and never reached the API (found 2026-09-02 — two pallets
  // "synced" on order 08737492 that the database had never seen). The reads
  // never had this problem because they build absolute URLs from baseURL.
  const apiOrigin = apiConfig.baseURL.replace(/\/api$/i, '')

  // FAKE while the API's barcoding endpoints don't exist: the whole pipeline
  // (enqueue → chip → flush → synced) runs for real against an accept-all
  // real API. Types without a live sender fall back to the stand-in.
  const realTransport = createHttpTransport({
    apiBase: apiOrigin,
    // The live log-in, and the one the work was recorded under when that one
    // has since run out (utils/syncQueue/httpTransport.ts, sessionHeaders).
    headers: transactions => sessionHeaders(getAccessToken(), transactions),
    onResult: reachedServer => {
      if (reachedServer) monitor.reportSuccess()
      else monitor.reportFailure()
    },
  })
  // Types with a live sender always go over HTTP. The stand-in only covers a
  // type whose endpoint does not exist yet, and only while FAKE_BACKEND is on
  // — today every registered type is live, so nothing reaches it.
  const transport: typeof realTransport = (type, batch) =>
    (FAKE_BACKEND && !isLiveType(type)) ? fakeSyncTransport(type, batch) : realTransport(type, batch)

  // No `refreshSession` on purpose: this app's auth store has no silent-refresh
  // action (a 401 clears the session), and with 600-minute access tokens a
  // token outlives a shift, so expiry mid-queue is not a practical case. The
  // engine's fallback is the safe one — a 401 keeps the work pending and
  // raises `needsAuth` so someone signs back in. Wire a refresh in here if one
  // is ever added to the store.
  const engine = createFlushEngine({ storage, transport })

  async function refreshCounts() {
    const counts = await engine.counts()
    pending.value = counts.pending
    needsAttention.value = counts.needsAttention
  }

  /** `ignoreBackoff` right after a log-in: work kept from a log-in that ran out goes now, not after its retry timer. */
  async function flush(options: { ignoreBackoff?: boolean } = {}) {
    if (isSyncing.value) return
    isSyncing.value = true
    try {
      const result = await engine.flush(options)
      needsAuth.value = result.needsAuth
    } finally {
      isSyncing.value = false
      await refreshCounts()
    }
  }

  /**
   * Commit an operator's transaction. Durable before this resolves; the returned id is
   * the external key the server will upsert on.
   */
  async function enqueue(type: string, payload: unknown): Promise<string> {
    const authStore = useAuthStore()
    const user = authStore.user
    // NOT crypto.randomUUID: it exists only in a secure context, and the
    // tablets load over plain http where it is undefined — it threw here,
    // inside the confirm, before any work was committed.
    const id = newUuid()

    const transaction: QueuedTransaction = {
      id,
      type,
      payload,
      // When the work HAPPENED — the server's CreatedDate will record when it
      // synced, which can be much later.
      occurredAtUtc: new Date().toISOString(),
      actor: {
        // Captured now. The drain-before-shift-end rule means the server's own
        // request-time stamp is already the right person, so this is belt and
        // braces — and it is what identifies the work if a power loss ever
        // strands rows from a previous session (PRD Open Question 1).
        name: user?.effectiveActorName || user?.fullName || null,
        username: user?.username ?? null,
      },
      // The log-in it was done under, so it is credited to this driver even if
      // that log-in runs out before the work is sent (types.ts).
      recordedSession: getAccessToken(),
      state: 'pending',
      attempts: 0,
      nextAttemptAt: 0,
      enqueuedAt: Date.now(),
    }

    await storage.put(transaction)
    await refreshCounts()
    void flush() // fire-and-forget: the operator never waits on the network
    return id
  }

  /**
   * Wait for the server's answer to ONE transaction, while it comes quickly.
   *
   * The queue still owns the work; this only watches the row a screen just
   * enqueued, so a refusal can go on the strip the moment it lands (decision
   * 0008 addendum, 2026-09-03) instead of into "to fix" after the driver has
   * moved on. Resolves `pending` when no answer is coming soon — offline, the
   * API unreachable, or slower than the timeout — and the queue carries on.
   */
  async function settle(id: string, timeoutMs = SETTLE_TIMEOUT_MS): Promise<Settlement> {
    return await awaitSettlement({
      read: async () => (await storage.all()).find(op => op.id === id),
      // The flush the enqueue fired may have been skipped by one already
      // running; asking again costs nothing when nothing is due.
      nudge: () => { void flush() },
      timeoutMs,
    })
  }

  async function list(): Promise<QueuedTransaction[]> {
    return await storage.all()
  }

  /** Rows the server refused, newest first — the needs-attention screen's data. */
  async function listNeedsAttention(): Promise<QueuedTransaction[]> {
    const rows = await storage.all()
    return rows.filter(row => row.state === 'needs-attention').reverse()
  }

  /** Requeue a refused row once its cause is dealt with, then push straight away. */
  async function retryTransaction(id: string): Promise<void> {
    await engine.retry(id)
    await refreshCounts()
    void flush()
  }

  /** Drop a refused row for good. */
  async function discardTransaction(id: string): Promise<void> {
    await engine.discard(id)
    await refreshCounts()
  }

  /**
   * Clear the queue at a session boundary.
   *
   * Called on BOTH log-in and log-out (user decision 2026-08-24). On log-out
   * the drain has already emptied it, so this is a no-op in the normal path; on
   * log-in it is the guarantee that matters — nothing a previous operator left
   * is ever submitted as the person logging in now.
   *
   * What is KEPT (Mark, 2026-10-15): work still waiting to be sent that
   * carries the log-in it was recorded under. It goes after the new log-in,
   * with that log-in beside the live one, and is credited to the driver who
   * did it. Everything else goes: refused work the last driver left in "to
   * fix", what was already sent, and waiting work from an older build that
   * carries no log-in and so could only be misattributed.
   */
  async function clearForSessionBoundary(): Promise<void> {
    for (const row of await storage.all()) {
      if (row.state === 'pending' && row.recordedSession) continue
      await storage.remove(row.id)
    }
    await refreshCounts()
  }

  /**
   * Before a new sign-in: send what the last session left behind, while its
   * token is still the one stored (utils/syncQueue/heldWorkFirst.ts has the
   * reasoning). The sign-in page calls this BEFORE it asks the server to sign
   * anyone in, and does not go on unless told to.
   */
  async function sendHeldWorkFirst(): Promise<HeldWorkOutcome> {
    isSyncing.value = true
    try {
      return await decideHeldWork({
        pending: async () => (await engine.counts()).pending,
        unattributed: async () => (await storage.all())
          .filter(row => row.state === 'pending' && !row.recordedSession).length,
        hasPreviousSession: () => Boolean(getAccessToken()),
        online: () => monitor.check(),
        drain: () => engine.drain(),
        // One more try tells why the drain stopped: a 401 is the server
        // refusing the old token, which no amount of waiting will change.
        sessionExpired: async () => (await engine.flush({ ignoreBackoff: true })).needsAuth,
      })
    } finally {
      isSyncing.value = false
      await refreshCounts()
    }
  }

  /**
   * Empty the queue before a shift ends.
   *
   * Sign-in and shift change require a live connection (PRD, Session
   * boundaries) precisely so this can run: queued work must never outlive the
   * session that made it, or the server — which stamps the actor from the token
   * at request time — would credit whoever signs in next.
   */
  async function drainForShiftEnd(): Promise<{ drained: boolean, pending: number, reason?: string }> {
    if (pending.value === 0) return { drained: true, pending: 0 }

    if (!monitor.isOnline.value) {
      // Ask the API directly before refusing — we may have gone offline in
      // state only, and making an operator walk to signal is expensive.
      await monitor.check()
    }
    if (!monitor.isOnline.value) {
      return {
        drained: false,
        pending: pending.value,
        reason: 'No connection — move into coverage before ending the shift.',
      }
    }

    isSyncing.value = true
    try {
      const result = await engine.drain()
      return result.drained
        ? { drained: true, pending: 0 }
        : {
            drained: false,
            pending: result.pending,
            reason: `${result.pending} offline transaction${result.pending > 1 ? 's' : ''} could not be sent yet — stay in coverage a moment longer.`,
          }
    } finally {
      isSyncing.value = false
      await refreshCounts()
    }
  }

  // Timers: probe while offline, and a slow safety-net flush while online.
  if (typeof window !== 'undefined') {
    window.setInterval(() => {
      if (!monitor.isOnline.value) void monitor.check()
    }, OFFLINE_PROBE_MS)

    window.setInterval(() => {
      if (monitor.isOnline.value && pending.value > 0) void flush()
    }, IDLE_FLUSH_MS)

    // Browser events are hints only — they make us probe sooner, never decide.
    window.addEventListener('online', () => { void monitor.hint() })
    window.addEventListener('offline', () => { void monitor.check() })

    void refreshCounts()
  }

  return {
    enqueue,
    settle,
    flush,
    drainForShiftEnd,
    sendHeldWorkFirst,
    clearForSessionBoundary,
    list,
    listNeedsAttention,
    retryTransaction,
    discardTransaction,
    refreshCounts,
    noteUnreachable,
    nudge,
    isOnline: monitor.isOnline,
    /** When the connection went, or null while there is one (utils/offlineRules.ts). */
    offlineSince: monitor.offlineSince,
    isSyncing: readonly(isSyncing),
    pending: readonly(pending),
    needsAttention: readonly(needsAttention),
    needsAuth: readonly(needsAuth),
    /** False when IndexedDB was unavailable — pending work won't survive a reload. */
    durable,
    hasPendingWork: computed(() => pending.value > 0),
  }
}

export function useSyncQueue() {
  if (!singleton) singleton = createQueueSingleton()
  return singleton
}
